import React, { useState, useEffect, useRef, useMemo } from "react";
import MaterialTable from "material-table";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from "common";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";
import { colors } from "../components/Theme/WebTheme";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Chip, Box } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";

export default function Promos() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const { editPromo } = api;
    const settings = useSelector((state) => state.settingsdata.settings);

    const inputRef = useRef(null);

    // Get available categories from settings and filter by active status
    const categorylistdata = useSelector((state) => state.categorylistdata);
    const getAvailableCategories = () => {
        let availableCategories = {};

        if (
            settings &&
            settings.ServicesEnabledKey &&
            settings.ServicesEnabledKey === "All"
        ) {
            availableCategories = {
                cab: t("cab"),
                delivery: t("delivery"),
                bid: t("bid"),
                rentals: t("rentals"),
                outstation: t("outstation"),
            };
        } else if (
            settings &&
            settings.ServicesEnabledKey &&
            settings.ServicesEnabledKey === "cab"
        ) {
            availableCategories = { cab: t("cab") };
        } else if (
            settings &&
            settings.ServicesEnabledKey &&
            settings.ServicesEnabledKey === "delivery"
        ) {
            availableCategories = { delivery: t("delivery") };
        } else if (
            settings &&
            settings.ServicesEnabledKey &&
            settings.ServicesEnabledKey === "bid"
        ) {
            availableCategories = { bid: t("bid") };
        } else if (
            settings &&
            settings.ServicesEnabledKey &&
            settings.ServicesEnabledKey === "rentals"
        ) {
            availableCategories = { rentals: t("rentals") };
        } else if (
            settings &&
            settings.ServicesEnabledKey &&
            settings.ServicesEnabledKey === "outstation"
        ) {
            availableCategories = { outstation: t("outstation") };
        } else {
            availableCategories = { cab: t("cab"), delivery: t("delivery") };
        }

        // Filter categories based on active status
        if (categorylistdata.categories) {
            const activeCategories = categorylistdata.categories.filter(
                (cat) => cat.active
            );
            const filteredCategories = {};

            Object.keys(availableCategories).forEach((key) => {
                const categoryExists = activeCategories.find(
                    (cat) => cat.category === key
                );
                if (categoryExists) {
                    filteredCategories[key] = availableCategories[key];
                }
            });

            return filteredCategories;
        }

        return availableCategories;
    };

    const categories = useMemo(
        () => getAvailableCategories(),
        [categorylistdata.categories, settings.ServicesEnabledKey, t]
    );

    // Get available payment methods and filter by active status
    const paymentmethods = useSelector((state) => state.paymentmethods);
    const getAvailablePaymentMethods = () => {
        const basePaymentMethods = {
            cash: t("cash"),
            wallet: t("wallet"),
            card: t("card"),
            yape: t("yape"),
        };

        // Filter payment methods based on active status in payment_settings
        if (paymentmethods.providers) {
            const activeProviders = paymentmethods.providers.filter(
                (provider) => provider.active
            );
            const filteredPaymentMethods = { wallet: t("wallet") }; // Wallet is always available

            // Add cash if not disabled in settings
            if (!settings.disable_cash) {
                filteredPaymentMethods.cash = t("cash");
            }

            // Add card if online payment is not disabled and there are active providers
            if (!settings.disable_online && activeProviders.length > 0) {
                filteredPaymentMethods.card = t("card");
            }

            // Add yape if not disabled in settings
            if (!settings.disable_yape) {
                filteredPaymentMethods.yape = t("yape");
            }

            return filteredPaymentMethods;
        }

        return basePaymentMethods;
    };

    const paymentMethods = useMemo(
        () => getAvailablePaymentMethods(),
        [
            paymentmethods.providers,
            settings.disable_cash,
            settings.disable_online,
            settings.disable_yape,
            t,
        ]
    );

    // Get available booking types
    const getAvailableBookingTypes = () => {
        return {
            "Book Now": t("book_now"),
            "Book Later": t("book_later"),
        };
    };
    const bookingTypes = useMemo(() => getAvailableBookingTypes(), [t]);

    // Get available car types/services and filter by active status
    const cartypes = useSelector((state) => state.cartypes.cars);
    const getAvailableServices = (selectedCategories = []) => {
        if (!cartypes) return {};
        const services = {};
        cartypes.forEach((carType) => {
            // Only show active car types/services
            if (carType.active) {
                // If categories are selected, filter services by category
                if (selectedCategories.length > 0) {
                    if (selectedCategories.includes(carType.category)) {
                        services[carType.id] = carType.name;
                    }
                } else {
                    // If no categories selected, show all active services
                    services[carType.id] = carType.name;
                }
            }
        });
        return services;
    };
    const services = useMemo(() => getAvailableServices(), [cartypes]);

    const columns = [
        {
            title: t("promo_name"),
            field: "promo_name",
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "left" },
        },
        {
            title: t("description"),
            field: "promo_description",
            cellStyle: { textAlign: "center" },
            headerStyle: { textAlign: "center" },
        },
        {
            title: t("type"),
            field: "promo_discount_type",
            lookup: { flat: t("flat"), percentage: t("percentage") },
            cellStyle: { textAlign: isRTL === "rtl" ? "right" : "left" },
        },
        {
            title: t("promo_discount_value"),
            field: "promo_discount_value",
            type: "numeric",
            cellStyle: { textAlign: "center" },
            headerStyle: { textAlign: "center" },
        },
        {
            title: t("max_limit"),
            field: "max_promo_discount_value",
            type: "numeric",
            cellStyle: { textAlign: "center" },
            headerStyle: { textAlign: "center" },
        },
        {
            title: t("min_limit"),
            field: "min_order",
            type: "numeric",
            cellStyle: { textAlign: "center" },
            headerStyle: { textAlign: "center" },
        },
        {
            title: t("categories"),
            field: "categories",
            render: (rowData) => {
                // When categories change, services will be automatically filtered
                // to only show services available for the selected categories
                const selectedCategories = rowData.categories || [];
                return (
                    <TextField
                        key={`categories-${
                            rowData.id || "new"
                        }-${JSON.stringify(selectedCategories)}`}
                        select
                        fullWidth
                        multiple
                        size="small"
                        value={selectedCategories}
                        disabled={true}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                    }}
                                >
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={categories[value] || value}
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            ),
                        }}
                    >
                        {Object.keys(categories).map((key) => (
                            <MenuItem key={key} value={key}>
                                {categories[key]}
                            </MenuItem>
                        ))}
                    </TextField>
                );
            },
            editComponent: (props) => {
                const selectedCategories = props.rowData.categories || [];
                const currentServices = props.rowData.services || [];

                return (
                    <TextField
                        key={`categories-edit-${
                            props.rowData.tableData?.id || "new"
                        }`}
                        select
                        fullWidth
                        multiple
                        value={selectedCategories}
                        onChange={(e) => {
                            const newValue = e.target.value;

                            // Filter out services that are no longer available based on selected categories
                            const filteredServices =
                                getAvailableServices(newValue);
                            const validServices = currentServices.filter(
                                (service) =>
                                    Object.keys(filteredServices).includes(
                                        service
                                    )
                            );

                            // Update categories
                            props.onChange(newValue);

                            // Also update services in the same row to remove invalid services
                            const updatedRowData = {
                                ...props.rowData,
                                categories: newValue,
                                services: validServices,
                            };

                            // Check if any services were removed due to category changes
                            const removedServices = currentServices.filter(
                                (service) =>
                                    !Object.keys(filteredServices).includes(
                                        service
                                    )
                            );

                            if (removedServices.length > 0) {
                                const removedServiceNames = removedServices
                                    .map(
                                        (service) =>
                                            getAvailableServices(
                                                props.rowData.categories || []
                                            )[service] || service
                                    )
                                    .join(", ");
                                console.log(
                                    `${t(
                                        "services_removed_due_to_category_change"
                                    )}: ${removedServiceNames}`
                                );

                                // Update the entire row data to reflect the changes
                                Object.assign(props.rowData, updatedRowData);
                            }
                        }}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                    }}
                                >
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={categories[value] || value}
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            ),
                        }}
                    >
                        {Object.keys(categories).map((key) => (
                            <MenuItem key={key} value={key}>
                                {categories[key]}
                            </MenuItem>
                        ))}
                    </TextField>
                );
            },
        },
        {
            title: t("services"),
            field: "services",
            render: (rowData) => {
                // Services are filtered based on selected categories
                // When categories are removed, related services are automatically removed
                const selectedServices = rowData.services || [];
                const selectedCategories = rowData.categories || [];
                const filteredServices =
                    getAvailableServices(selectedCategories);

                return (
                    <TextField
                        key={`services-${rowData.id || "new"}-${JSON.stringify(
                            selectedServices
                        )}-${JSON.stringify(selectedCategories)}`}
                        select
                        fullWidth
                        multiple
                        size="small"
                        value={selectedServices}
                        disabled={true}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                    }}
                                >
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={
                                                filteredServices[value] || value
                                            }
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            ),
                        }}
                    >
                        {Object.keys(filteredServices).map((key) => (
                            <MenuItem key={key} value={key}>
                                {filteredServices[key]}
                            </MenuItem>
                        ))}
                    </TextField>
                );
            },
            editComponent: (props) => {
                const selectedCategories = props.rowData.categories || [];
                const filteredServices =
                    getAvailableServices(selectedCategories);

                // Filter selected services to only include valid ones based on current categories
                const validSelectedServices = (
                    props.rowData.services || []
                ).filter((service) =>
                    Object.keys(filteredServices).includes(service)
                );

                // If there are invalid services, update the row data immediately
                const currentServices = props.rowData.services || [];
                if (currentServices.length !== validSelectedServices.length) {
                    // Update the row data to remove invalid services
                    props.rowData.services = validSelectedServices;

                    // Log the removed services for debugging
                    const removedServices = currentServices.filter(
                        (service) =>
                            !Object.keys(filteredServices).includes(service)
                    );
                    if (removedServices.length > 0) {
                        console.log(
                            `Invalid services removed: ${removedServices.join(
                                ", "
                            )}`
                        );
                    }
                }

                return (
                    <TextField
                        key={`services-edit-${
                            props.rowData.tableData?.id || "new"
                        }`}
                        select
                        fullWidth
                        multiple
                        value={validSelectedServices}
                        onChange={(e) => {
                            props.onChange(e.target.value);
                        }}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                    }}
                                >
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={
                                                filteredServices[value] || value
                                            }
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            ),
                        }}
                    >
                        {Object.keys(filteredServices).map((key) => (
                            <MenuItem key={key} value={key}>
                                {filteredServices[key]}
                            </MenuItem>
                        ))}
                    </TextField>
                );
            },
        },
        {
            title: t("payment_methods"),
            field: "payment_methods",
            render: (rowData) => {
                const selectedPaymentMethods = rowData.payment_methods || [];

                return (
                    <TextField
                        key={`payment-methods-${
                            rowData.id || "new"
                        }-${JSON.stringify(selectedPaymentMethods)}`}
                        select
                        fullWidth
                        multiple
                        size="small"
                        value={selectedPaymentMethods}
                        disabled={true}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                    }}
                                >
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={
                                                paymentMethods[value] || value
                                            }
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            ),
                        }}
                    >
                        {Object.keys(paymentMethods).map((key) => (
                            <MenuItem key={key} value={key}>
                                {paymentMethods[key]}
                            </MenuItem>
                        ))}
                    </TextField>
                );
            },
            editComponent: (props) => {
                const selectedPaymentMethods =
                    props.rowData.payment_methods || [];

                return (
                    <TextField
                        key={`payment-methods-edit-${
                            props.rowData.tableData?.id || "new"
                        }`}
                        select
                        fullWidth
                        multiple
                        value={selectedPaymentMethods}
                        onChange={(e) => {
                            props.onChange(e.target.value);
                        }}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                    }}
                                >
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={
                                                paymentMethods[value] || value
                                            }
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            ),
                        }}
                    >
                        {Object.keys(paymentMethods).map((key) => (
                            <MenuItem key={key} value={key}>
                                {paymentMethods[key]}
                            </MenuItem>
                        ))}
                    </TextField>
                );
            },
        },
        {
            title: t("booking_types"),
            field: "booking_types",
            render: (rowData) => {
                const selectedBookingTypes = rowData.booking_types || [];

                return (
                    <TextField
                        key={`booking-types-${
                            rowData.id || "new"
                        }-${JSON.stringify(selectedBookingTypes)}`}
                        select
                        fullWidth
                        multiple
                        size="small"
                        value={selectedBookingTypes}
                        disabled={true}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                    }}
                                >
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={bookingTypes[value] || value}
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            ),
                        }}
                    >
                        {Object.keys(bookingTypes).map((key) => (
                            <MenuItem key={key} value={key}>
                                {bookingTypes[key]}
                            </MenuItem>
                        ))}
                    </TextField>
                );
            },
            editComponent: (props) => {
                const selectedBookingTypes = props.rowData.booking_types || [];

                return (
                    <TextField
                        key={`booking-types-edit-${
                            props.rowData.tableData?.id || "new"
                        }`}
                        select
                        fullWidth
                        multiple
                        value={selectedBookingTypes}
                        onChange={(e) => {
                            props.onChange(e.target.value);
                        }}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                    }}
                                >
                                    {selected.map((value) => (
                                        <Chip
                                            key={value}
                                            label={bookingTypes[value] || value}
                                            size="small"
                                        />
                                    ))}
                                </Box>
                            ),
                        }}
                    >
                        {Object.keys(bookingTypes).map((key) => (
                            <MenuItem key={key} value={key}>
                                {bookingTypes[key]}
                            </MenuItem>
                        ))}
                    </TextField>
                );
            },
        },
        {
            title: t("end_date"),
            field: "promo_validity",
            editComponent: (props) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                        inputRef={inputRef}
                        renderInput={(props) => <TextField {...props} />}
                        label="DateTimePicker"
                        value={
                            props.rowData.promo_validity
                                ? props.rowData.promo_validity
                                : new Date()
                        }
                        onChange={(newValue) => {
                            props.onChange(newValue);
                        }}
                        autoFocus={false}
                    />
                </LocalizationProvider>
            ),
            render: (rowData) =>
                rowData.promo_validity
                    ? moment(rowData.promo_validity).format("lll")
                    : null,
            cellStyle: { textAlign: "center", width: 200 },
            headerStyle: { textAlign: "center" },
        },
        {
            title: t("promo_usage"),
            field: "promo_usage_limit",
            type: "numeric",
            cellStyle: { textAlign: "center" },
            headerStyle: { textAlign: "center" },
        },
        {
            title: t("promo_used_by"),
            field: "user_avail",
            editable: "never",
            cellStyle: { textAlign: "center" },
            headerStyle: { textAlign: "center" },
        },
    ];

    const [data, setData] = useState([]);
    const promodata = useSelector((state) => state.promodata);
    const dispatch = useDispatch();

    useEffect(() => {
        if (promodata.promos) {
            setData(promodata.promos);
        } else {
            setData([]);
        }
    }, [promodata.promos]);

    const [selectedRow, setSelectedRow] = useState(null);

    return promodata.loading ? (
        <CircularLoading />
    ) : (
        <MaterialTable
            title={t("promo_offer")}
            columns={columns}
            style={{
                direction: isRTL === "rtl" ? "rtl" : "ltr",
                borderRadius: "8px",
                boxShadow: "4px 4px 6px #9E9E9E",
            }}
            data={data}
            options={{
                rowStyle: (rowData) => ({
                    backgroundColor:
                        selectedRow === rowData.tableData.id
                            ? colors.THIRDCOLOR
                            : colors.WHITE,
                }),
                editable: {
                    backgroundColor: colors.WHITE,
                    fontSize: "0.8em",
                    fontWeight: "bold ",
                    fontFamily: 'Lucida Console", "Courier New", monospace',
                },
                headerStyle: {
                    position: "sticky",
                    top: "0px",
                    backgroundColor: colors.SECONDARY,
                    color: colors.WHITE,
                    fontSize: "0.8em",
                    fontWeight: "bold ",
                    fontFamily: 'Lucida Console", "Courier New", monospace',
                },
            }}
            localization={{
                body: {
                    addTooltip: t("add"),
                    deleteTooltip: t("delete"),
                    editTooltip: t("edit"),
                    emptyDataSourceMessage: t("blank_message"),
                    editRow: {
                        deleteText: t("delete_message"),
                        cancelTooltip: t("cancel"),
                        saveTooltip: t("save"),
                    },
                },
                toolbar: {
                    searchPlaceholder: t("search"),
                    exportTitle: t("export"),
                },
                header: {
                    actions: t("actions"),
                },
                pagination: {
                    labelDisplayedRows: "{from}-{to} " + t("of") + " {count}",
                    firstTooltip: t("first_page_tooltip"),
                    previousTooltip: t("previous_page_tooltip"),
                    nextTooltip: t("next_page_tooltip"),
                    lastTooltip: t("last_page_tooltip"),
                },
            }}
            editable={{
                onRowAdd: (newData) =>
                    new Promise((resolve, reject) => {
                        setTimeout(() => {
                            if (
                                !(
                                    newData.promo_name &&
                                    newData.promo_description &&
                                    newData.promo_discount_value &&
                                    newData.promo_discount_type &&
                                    newData.promo_discount_value &&
                                    newData.max_promo_discount_value &&
                                    newData.min_order &&
                                    newData.promo_usage_limit &&
                                    newData.min_order >=
                                        newData.max_promo_discount_value &&
                                    newData.promo_discount_value >=
                                        newData.max_promo_discount_value
                                )
                            ) {
                                alert(t("no_details_error"));
                                reject();
                            } else {
                                newData["createdAt"] = new Date().getTime();
                                newData["promo_validity"] =
                                    newData.promo_validity
                                        ? new Date(
                                              newData.promo_validity
                                          ).getTime()
                                        : null;

                                // Clean up services to only include valid ones based on selected categories
                                if (
                                    newData.categories &&
                                    newData.categories.length > 0 &&
                                    newData.services &&
                                    newData.services.length > 0
                                ) {
                                    const availableServices =
                                        getAvailableServices(
                                            newData.categories
                                        );
                                    const validServices =
                                        newData.services.filter((service) =>
                                            Object.keys(
                                                availableServices
                                            ).includes(service)
                                        );

                                    // Log if any services were removed
                                    const removedServices =
                                        newData.services.filter(
                                            (service) =>
                                                !Object.keys(
                                                    availableServices
                                                ).includes(service)
                                        );
                                    if (removedServices.length > 0) {
                                        console.log(
                                            `Services cleaned up during add: ${removedServices.join(
                                                ", "
                                            )}`
                                        );
                                    }

                                    newData.services = validServices;
                                } else if (
                                    !newData.categories ||
                                    newData.categories.length === 0
                                ) {
                                    // If no categories selected, clear all services
                                    newData.services = [];
                                }

                                // Set default values for new fields
                                newData["category_specific"] =
                                    newData.categories &&
                                    newData.categories.length > 0;
                                newData["payment_method_specific"] =
                                    newData.payment_methods &&
                                    newData.payment_methods.length > 0;
                                newData["service_specific"] =
                                    newData.services &&
                                    newData.services.length > 0;
                                newData["booking_type_specific"] =
                                    newData.booking_types &&
                                    newData.booking_types.length > 0;

                                dispatch(editPromo(newData, "Add"));
                                resolve();
                            }
                        }, 600);
                    }),
                onRowUpdate: (newData, oldData) =>
                    settings.AllowCriticalEditsAdmin
                        ? new Promise((resolve, reject) => {
                              setTimeout(() => {
                                  if (
                                      !(
                                          newData.promo_name &&
                                          newData.promo_description &&
                                          newData.promo_discount_value &&
                                          newData.promo_discount_type &&
                                          newData.promo_discount_value &&
                                          newData.max_promo_discount_value &&
                                          newData.min_order &&
                                          newData.promo_usage_limit &&
                                          newData.min_order >=
                                              newData.max_promo_discount_value &&
                                          newData.promo_discount_value >=
                                              newData.max_promo_discount_value
                                      )
                                  ) {
                                      alert(t("no_details_error"));
                                      reject();
                                  } else {
                                      resolve();
                                      newData["promo_validity"] =
                                          newData.promo_validity
                                              ? new Date(
                                                    newData.promo_validity
                                                ).getTime()
                                              : null;

                                      // Clean up services to only include valid ones based on selected categories
                                      if (
                                          newData.categories &&
                                          newData.categories.length > 0 &&
                                          newData.services &&
                                          newData.services.length > 0
                                      ) {
                                          const availableServices =
                                              getAvailableServices(
                                                  newData.categories
                                              );
                                          const validServices =
                                              newData.services.filter(
                                                  (service) =>
                                                      Object.keys(
                                                          availableServices
                                                      ).includes(service)
                                              );

                                          // Log if any services were removed
                                          const removedServices =
                                              newData.services.filter(
                                                  (service) =>
                                                      !Object.keys(
                                                          availableServices
                                                      ).includes(service)
                                              );
                                          if (removedServices.length > 0) {
                                              console.log(
                                                  `Services cleaned up during save: ${removedServices.join(
                                                      ", "
                                                  )}`
                                              );
                                          }

                                          newData.services = validServices;
                                      } else if (
                                          !newData.categories ||
                                          newData.categories.length === 0
                                      ) {
                                          // If no categories selected, clear all services
                                          newData.services = [];
                                      }

                                      // Update specific flags
                                      newData["category_specific"] =
                                          newData.categories &&
                                          newData.categories.length > 0;
                                      newData["payment_method_specific"] =
                                          newData.payment_methods &&
                                          newData.payment_methods.length > 0;
                                      newData["service_specific"] =
                                          newData.services &&
                                          newData.services.length > 0;
                                      newData["booking_type_specific"] =
                                          newData.booking_types &&
                                          newData.booking_types.length > 0;

                                      if (newData !== oldData) {
                                          delete newData.tableData;
                                          dispatch(
                                              editPromo(newData, "Update")
                                          );
                                      }
                                  }
                              }, 600);
                          })
                        : new Promise((resolve) => {
                              setTimeout(() => {
                                  resolve();
                                  alert(t("demo_mode"));
                              }, 600);
                          }),
                onRowDelete: (oldData) =>
                    settings.AllowCriticalEditsAdmin
                        ? new Promise((resolve) => {
                              setTimeout(() => {
                                  resolve();
                                  dispatch(editPromo(oldData, "Delete"));
                              }, 600);
                          })
                        : new Promise((resolve) => {
                              setTimeout(() => {
                                  resolve();
                                  alert(t("demo_mode"));
                              }, 600);
                          }),
            }}
        />
    );
}
